import {
  Box,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from "@mui/material";
import {
  ChevronRight,
  ChevronDown,
  Building,
  Package,
  DollarSign,
  ChartBar,
  Target,
  Goal,
  Info,
} from "lucide-react";

const SectionHeader = ({
  id,
  title,
  icon: Icon,
  expandedSection,
  setExpandedSection,
}: any) => (
  <Box
    onClick={() => setExpandedSection(expandedSection === id ? "" : id)}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      p: 2,
      bgcolor: "#000000",
      color: "white",
      borderRadius: "12px",
      cursor: "pointer",
      mb: 1,
      "&:hover": {
        bgcolor: "#1f1f1f",
      },
      transition: "all 0.3s ease",
      boxShadow:
        expandedSection === id ? "0 4px 6px -1px rgba(0, 0, 0, 0.1)" : "none",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/10">
        <Icon className="h-5 w-5 text-white" />
      </div>
      <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
    {expandedSection === id ? (
      <ChevronDown className="h-5 w-5" />
    ) : (
      <ChevronRight className="h-5 w-5" />
    )}
  </Box>
);

const FormSection = ({ id, children, expandedSection }: any) => (
  <Box
    sx={{
      mb: 3,
      display: expandedSection === id ? "block" : "none",
      transition: "all 0.3s ease",
      transform: expandedSection === id ? "translateY(0)" : "translateY(-10px)",
      opacity: expandedSection === id ? 1 : 0,
    }}
  >
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: "12px",
        p: 3,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        border: "1px solid rgba(229, 231, 235, 0.8)",
      }}
    >
      {children}
    </Box>
  </Box>
);

const InputField = ({
  section,
  field,
  label,
  type = "text",
  options = [],
  isArray = false,
  isNested = false,
  parentField = "",
  value,
  onChange,
  required = false,
}: any) => {
  const handleChange = (event: Event | any) => {
    onChange(
      section,
      field,
      event.target.value,
      isNested ? parentField : undefined
    );
  };

  return (
    <Box sx={{ mb: 2.5 }}>
      {type === "select" ? (
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel
            id={`${section}-${field}-label`}
            sx={{
              fontSize: "0.875rem",
              background: "white",
              padding: "0 4px",
            }}
          >
            {label}
            {required && " *"}
          </InputLabel>
          <Select
            labelId={`${section}-${field}-label`}
            label={label + (required ? " *" : "")}
            value={value || ""}
            onChange={handleChange}
            sx={{
              borderRadius: "8px",
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(229, 231, 235, 0.8)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(156, 163, 175, 0.8)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ef4444",
              },
            }}
          >
            <MenuItem
              value=""
              sx={{ color: "text.secondary", fontStyle: "italic" }}
            >
              <em>Select an option</em>
            </MenuItem>
            {options.map((opt: any) => (
              <MenuItem
                key={opt}
                value={opt}
                sx={{ borderRadius: "4px", my: 0.5, mx: 0.5 }}
              >
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : type === "textarea" ? (
        <TextField
          label={label + (required ? " *" : "")}
          multiline
          rows={4}
          fullWidth
          variant="outlined"
          value={value || ""}
          onChange={handleChange}
          size="small"
          InputProps={{
            sx: {
              borderRadius: "8px",
              fontSize: "0.875rem",
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(229, 231, 235, 0.8)",
              },
            },
          }}
          InputLabelProps={{
            sx: {
              fontSize: "0.875rem",
            },
          }}
          helperText={isArray ? "Separate multiple items with commas" : ""}
          FormHelperTextProps={{
            sx: {
              display: "flex",
              alignItems: "center",
              mt: 0.5,
              fontSize: "0.75rem",
              color: "text.secondary",
            },
          }}
        />
      ) : (
        <TextField
          label={label + (required ? " *" : "")}
          type={type}
          fullWidth
          variant="outlined"
          value={value || ""}
          onChange={handleChange}
          size="small"
          InputProps={{
            sx: {
              borderRadius: "8px",
              fontSize: "0.875rem",
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(229, 231, 235, 0.8)",
              },
            },
          }}
          InputLabelProps={{
            sx: {
              fontSize: "0.875rem",
            },
          }}
        />
      )}
    </Box>
  );
};

// Basic Company Information Section
export const BasicCompanyInfoSection = ({
  formData,
  handleChange,
  handleArrayChange,
  expandedSection,
  setExpandedSection,
}: any) => {
  return (
    <>
      <SectionHeader
        id="basic"
        title="Basic Company Information"
        icon={Building}
        expandedSection={expandedSection}
        setExpandedSection={setExpandedSection}
      />
      <FormSection id="basic" expandedSection={expandedSection}>
        <div className="bg-red-50 p-3 rounded-lg mb-4 flex items-start gap-2">
          <Info className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <Typography
            variant="body2"
            sx={{ color: "#b91c1c", fontSize: "0.875rem" }}
          >
            This section captures the foundational details about your company
            that will help us understand your business context.
          </Typography>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <InputField
            section="basicCompanyInfo"
            field="companyName"
            label="Company Name"
            value={formData.basicCompanyInfo.companyName}
            onChange={handleChange}
            required={true}
          />
          <InputField
            section="basicCompanyInfo"
            field="industrySector"
            label="Industry/Sector"
            value={formData.basicCompanyInfo.industrySector}
            onChange={handleChange}
            required={true}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <InputField
            section="basicCompanyInfo"
            field="companySize"
            label="Company Size (number of employees)"
            type="number"
            value={formData.basicCompanyInfo.companySize}
            onChange={handleChange}
            required={true}
          />
          <InputField
            section="basicCompanyInfo"
            field="annualRevenueRange"
            label="Annual Revenue Range"
            type="select"
            options={[
              "Under $100K",
              "$100K-$500K",
              "$500K-$1M",
              "$1M-$5M",
              "$5M+",
            ]}
            value={formData.basicCompanyInfo.annualRevenueRange}
            onChange={handleChange}
            required={true}
          />
        </div>

        <InputField
          section="basicCompanyInfo"
          field="geographicLocations"
          label="Geographic Location(s) (ex: USA,Canada,etc.)"
          isArray={true}
          value={formData.basicCompanyInfo.geographicLocations}
          onChange={handleArrayChange}
          required={true}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <InputField
            section="basicCompanyInfo"
            field="yearsInBusiness"
            label="Years in Business"
            type="number"
            value={formData.basicCompanyInfo.yearsInBusiness}
            onChange={handleChange}
            required={true}
          />
          <InputField
            section="basicCompanyInfo"
            field="currentGrowthStage"
            label="Current Growth Stage"
            type="select"
            options={["Pre-seed", "Seed", "Series A", "Series B+", "Mature"]}
            value={formData.basicCompanyInfo.currentGrowthStage}
            onChange={handleChange}
            required={true}
          />
        </div>

        <InputField
          section="basicCompanyInfo"
          field="websiteUrl"
          label="Website URL"
          value={formData.basicCompanyInfo.websiteUrl}
          onChange={handleChange}
          required={true}
        />
      </FormSection>
    </>
  );
};

// Product Service Details Section
export const ProductServiceDetailsSection = ({
  formData,
  handleChange,
  handleArrayChange,
  expandedSection,
  setExpandedSection,
}: any) => {
  return (
    <>
      <SectionHeader
        id="product"
        title="Product/Service Details"
        icon={Package}
        expandedSection={expandedSection}
        setExpandedSection={setExpandedSection}
      />
      <FormSection id="product" expandedSection={expandedSection}>
        <div className="bg-blue-50 p-3 rounded-lg mb-4 flex items-start gap-2">
          <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <Typography
            variant="body2"
            sx={{ color: "#1e40af", fontSize: "0.875rem" }}
          >
            Details about your products or services help our AI agents
            understand your unique value proposition.
          </Typography>
        </div>

        <InputField
          section="productServiceDetails"
          field="coreDescription"
          label="Core Product/Service Description"
          type="textarea"
          value={formData.productServiceDetails.coreDescription}
          onChange={handleChange}
          required={true}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <InputField
            section="productServiceDetails"
            field="currentProductStage"
            label="Current Product Stage"
            type="select"
            options={["Idea/Concept", "MVP", "Beta", "Launched", "Scaling"]}
            value={formData.productServiceDetails.currentProductStage}
            onChange={handleChange}
            required={true}
          />
          <InputField
            section="productServiceDetails"
            field="deliveryMethod"
            label="Delivery Method"
            type="select"
            options={[
              "SaaS",
              "Physical Product",
              "Digital Product",
              "Service",
              "Hybrid",
            ]}
            value={formData.productServiceDetails.deliveryMethod}
            onChange={handleChange}
            required={true}
          />
        </div>
        <InputField
          section="productServiceDetails"
          field="primaryValueProposition"
          label="Primary Value Proposition"
          type="textarea"
          value={formData.productServiceDetails.primaryValueProposition}
          onChange={handleChange}
          required={true}
        />
        <InputField
          section="productServiceDetails"
          field="keyFeaturesBenefits"
          label="Key Features/Benefits"
          type="textarea"
          isArray={true}
          value={formData.productServiceDetails.keyFeaturesBenefits}
          onChange={handleArrayChange}
          required={true}
        />
      </FormSection>
    </>
  );
};

// Market Position Section
export const MarketPositionSection = ({
  formData,
  handleChange,
  handleArrayChange,
  expandedSection,
  setExpandedSection,
}: any) => {
  return (
    <>
      <SectionHeader
        id="market"
        title="Current Market Position"
        icon={Target}
        expandedSection={expandedSection}
        setExpandedSection={setExpandedSection}
      />
      <FormSection id="market" expandedSection={expandedSection}>
        <div className="bg-green-50 p-3 rounded-lg mb-4 flex items-start gap-2">
          <Info className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
          <Typography
            variant="body2"
            sx={{ color: "#166534", fontSize: "0.875rem" }}
          >
            Understanding your place in the market helps our AI agents identify
            optimal positioning strategies.
          </Typography>
        </div>

        <InputField
          section="currentMarketPosition"
          field="targetMarketDescription"
          label="Target Market Description"
          type="textarea"
          value={formData.currentMarketPosition.targetMarketDescription}
          onChange={handleChange}
          required={true}
        />
        <InputField
          section="currentMarketPosition"
          field="mainCompetitors"
          label="Main Competitors"
          type="textarea"
          isArray={true}
          value={formData.currentMarketPosition.mainCompetitors}
          onChange={handleArrayChange}
          required={true}
        />
        <InputField
          section="currentMarketPosition"
          field="marketDifferentiators"
          label="Market Differentiators"
          type="textarea"
          value={formData.currentMarketPosition.marketDifferentiators}
          onChange={handleChange}
          required={true}
        />
      </FormSection>
    </>
  );
};

// Customer Success Section
export const CustomerSuccessSection = ({
  formData,
  handleChange,
  handleArrayChange,
  expandedSection,
  setExpandedSection,
}: any) => {
  return (
    <>
      <SectionHeader
        id="metrics"
        title="Customer Success Metrics"
        icon={ChartBar}
        expandedSection={expandedSection}
        setExpandedSection={setExpandedSection}
      />
      <FormSection id="metrics" expandedSection={expandedSection}>
        <div className="bg-purple-50 p-3 rounded-lg mb-4 flex items-start gap-2">
          <Info className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
          <Typography
            variant="body2"
            sx={{ color: "#7e22ce", fontSize: "0.875rem" }}
          >
            Insight into how customers benefit from your offerings helps
            identify ideal target audiences.
          </Typography>
        </div>

        <InputField
          section="customerSuccessMetrics"
          field="commonPainPointsSolved"
          label="Common Pain Points Solved"
          type="textarea"
          isArray={true}
          value={formData.customerSuccessMetrics.commonPainPointsSolved}
          onChange={handleArrayChange}
          required={true}
        />
        <InputField
          section="customerSuccessMetrics"
          field="typicalRoi"
          label="Typical ROI"
          value={formData.customerSuccessMetrics.typicalRoi}
          onChange={handleChange}
          required={true}
        />
      </FormSection>
    </>
  );
};

// Business Goals Section
export const BusinessGoalsSection = ({
  formData,
  handleChange,
  handleArrayChange,
  expandedSection,
  setExpandedSection,
}: any) => {
  return (
    <>
      <SectionHeader
        id="goals"
        title="Business Goals"
        icon={Goal}
        expandedSection={expandedSection}
        setExpandedSection={setExpandedSection}
      />
      <FormSection id="goals" expandedSection={expandedSection}>
        <div className="bg-amber-50 p-3 rounded-lg mb-4 flex items-start gap-2">
          <Info className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <Typography
            variant="body2"
            sx={{ color: "#92400e", fontSize: "0.875rem" }}
          >
            Your growth objectives help us align targeting strategies with your
            business trajectory.
          </Typography>
        </div>

        <InputField
          section="businessGoals"
          field="shortTermRevenueTargets"
          label="Short-term Revenue Targets"
          value={formData.businessGoals.shortTermRevenueTargets}
          onChange={handleChange}
          required={true}
        />
        <InputField
          section="businessGoals"
          field="longTermGrowthObjectives"
          label="Long-term Growth Objectives"
          type="textarea"
          value={formData.businessGoals.longTermGrowthObjectives}
          onChange={handleChange}
          required={true}
        />
        <InputField
          section="businessGoals"
          field="targetMarketExpansion"
          label="Target Market Expansion"
          type="textarea"
          value={formData.businessGoals.targetMarketExpansion}
          onChange={handleChange}
          required={true}
        />
      </FormSection>
    </>
  );
};

// Budget Resources Section
export const BudgetResourcesSection = ({
  formData,
  handleChange,
  expandedSection,
  setExpandedSection,
}: any) => (
  <>
    <SectionHeader
      id="budget"
      title="Budget and Resource Allocation"
      icon={DollarSign}
      expandedSection={expandedSection}
      setExpandedSection={setExpandedSection}
    />
    <FormSection id="budget" expandedSection={expandedSection}>
      <div className="bg-rose-50 p-3 rounded-lg mb-4 flex items-start gap-2">
        <Info className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
        <Typography
          variant="body2"
          sx={{ color: "#be123c", fontSize: "0.875rem" }}
        >
          Understanding your financial parameters helps create realistic and
          effective campaign strategies.
        </Typography>
      </div>

      <InputField
        section="budgetResourceAllocation"
        field="customerAcquisitionCostTargets"
        label="Customer Acquisition Cost Targets"
        value={formData.budgetResourceAllocation.customerAcquisitionCostTargets}
        onChange={handleChange}
        required={true}
      />
      <InputField
        section="budgetResourceAllocation"
        field="marketingBudget"
        label="Marketing Budget"
        value={formData.budgetResourceAllocation.marketingBudget}
        onChange={handleChange}
        required={true}
      />
    </FormSection>
  </>
);
